/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useMemo } from 'react';
import rtlPlugin from 'stylis-plugin-rtl';
import { StyleSheetManager } from 'styled-components';
import path from 'path';
import { ModelManager } from '@adobe/aem-spa-page-model-manager';
import { useReportWebVitals } from 'next/web-vitals';

import '@glidejs/glide/src/assets/sass/glide.core.scss';
import '@glidejs/glide/src/assets/sass/glide.theme.scss';
import '../styles/homepage.scss'; // TODO: once new Global nav implemention complete we have to remove this

import { defineComponentMapping } from '../import-components';
import {
  CustomModelClient,
  canUseDOM,
  respGridUtil,
  populatePIdataLayer,
  UserSessionProvider,
  DynamicDCAPageProvider,
  getEnvProp,
} from '@marriott/mi-headless-utils';
import {
  useClientEnvVarsStore,
  usePersistentGlobalStore,
  useDCAPageModelStore,
  useScrollStore,
} from '@marriott/mi-store-utils';
import { setSubDirectoryPrefix } from '@marriott/mi-shop-components/utils';
import { useRouter } from 'next/router';
import { version } from '@marriott/global-styles/package.json';
import { envVarKeys } from '../_constants/envVarKeys';

path.resolve('./next.config.js');

declare global {
  interface Window {
    jQuery: unknown;
    $: any;
    deployedEnvType: string;
  }
}

const paramsString = canUseDOM && window.location.search;
const searchParams = new URLSearchParams(paramsString);
const modelClient = new CustomModelClient(process.env.NEXT_PUBLIC_AEM_HOST);

if (process.env.NODE_ENV !== 'test') {
  ModelManager.initializeAsync(
    !(searchParams?.get('wcmmode') === 'disabled') && {
      modelClient,
    }
  );
}

const App = function (props: any) {
  const { Component, pageProps } = props;
  const rtlPluginTs = (...args: unknown[]) => {
    return rtlPlugin(args[0] as number, args[1] as string);
  };
  const {
    model,
    pagePath,
    isAuthorMode,
    requestId,
    currentLocale,
    apolloEnvVars,
    NEXT_PUBLIC_FETCH_OFFERS_API_URL,
    TARGET_PROPERTY_TOKEN,
    NEXT_GLOBAL_ASSET_PREFIX,
    featureFlagLogo,
  } = pageProps;

  let { featureFlags } = pageProps;

  // Feature flag: when true, defers synchronous initialization work to useEffect
  // to reduce pre-hydration blocking. When false, runs original synchronous code.
  // fetchFlag returns { flag_name: value }, so extract the boolean from the object.
  const deferHydration = pageProps?.featureFlagDeferHydration?.experience_mcom_homepage_defer_hydration === true;

  const { query } = useRouter();

  // This brandCode is just for local development and testing. It supplies the brandCode to load brandCSS below.
  // Do not use for production features!
  const devBrandCode = query.brandCode;

  // next performance analytics for more info: https://nextjs.org/docs/pages/api-reference/functions/use-report-web-vitals
  useReportWebVitals(metric => {
    // do not render performance analytics to the console in prod
    if (/dev|test|uat|perf/i.test(pageProps?.DEPLOYED_ENV_CONFIG)) {
      console.log('next/web-vitals - Metric: %s', metric.name, {
        metric,
      });
    }
  });

  const isWebSDKEnabled = getEnvProp(envVarKeys.NEXT_PUBLIC_ENABLE_WEBSDK) === 'true' && model?.enableWebSDK === 'true';
  const responsivegrid = model?.cqItems?.root?.[':items']?.responsivegrid;
  const prodDNS = 'https://www.marriott.com';

  // Get store actions
  const setPageModel = useDCAPageModelStore(state => state.setPageModel);
  const setEnvVars = useClientEnvVarsStore(state => state.setEnvVars);
  const mergeEnvVars = useClientEnvVarsStore((state: any) => state.mergeEnvVars);
  const initialize = useScrollStore(state => state.initialize);
  const updateGlobalData = usePersistentGlobalStore((state: any) => state.updateGlobalData);

  // Track if component mapping has been initialized (used when flag is ON)
  const componentMappingInitialized = useRef(false);

  // =========================================================================
  // Hooks must be called unconditionally (Rules of Hooks).
  // When deferHydration is ON, these memoized values are used in the
  // synchronous deferHydration block below. When OFF, the memoized values
  // are unused — the synchronous path operates on its own local variables.
  // =========================================================================

  const respGridModel = useMemo(() => respGridUtil(responsivegrid), [responsivegrid]);

  const deferredEnvVars = useMemo(() => {
    const vars = apolloEnvVars ? { ...apolloEnvVars } : {};
    vars['NEXT_PUBLIC_FETCH_OFFERS_API_URL'] = NEXT_PUBLIC_FETCH_OFFERS_API_URL;
    vars['TARGET_PROPERTY_TOKEN'] = TARGET_PROPERTY_TOKEN;
    vars['SESSION_APP_POST_CALL_URL'] = pageProps?.SESSION_APP_POST_CALL_URL;
    vars['NEXT_PUBLIC_PREFIX'] = pageProps?.NEXT_PUBLIC_PREFIX;
    vars['NEXT_PUBLIC_ENABLE_DCA_V2'] = getEnvProp(envVarKeys.NEXT_PUBLIC_ENABLE_DCA_V2);
    vars['NEXT_PUBLIC_ENABLE_WEBSDK'] = isWebSDKEnabled;
    vars['GET_DESTINATION_TYPE_FLAG'] = pageProps?.GET_DESTINATION_TYPE_FLAG;
    return vars;
  }, [
    apolloEnvVars,
    NEXT_PUBLIC_FETCH_OFFERS_API_URL,
    TARGET_PROPERTY_TOKEN,
    pageProps?.SESSION_APP_POST_CALL_URL,
    pageProps?.NEXT_PUBLIC_PREFIX,
    pageProps?.GET_DESTINATION_TYPE_FLAG,
    isWebSDKEnabled,
  ]);

  const deferredMergedEnvVars = useMemo(
    () => ({
      SESSION_GET_CALL_URL_CLIENT: pageProps?.SESSION_GET_CALL_URL_CLIENT,
      NEXT_PUBLIC_SUBMIT_SEARCH_URL: pageProps?.NEXT_PUBLIC_SUBMIT_SEARCH_URL,
      NEXT_PUBLIC_AVAILABILITY_SEARCH_URL: pageProps?.NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
      GOOGLE_MAP_API_KEY: pageProps?.GOOGLE_MAP_API_KEY,
      UXL_IMAGE_CACHE_DOMAIN: pageProps?.UXL_IMAGE_CACHE_DOMAIN,
      SESSION_APP_GET_CALL_V1_URL: pageProps?.SESSION_APP_GET_CALL_V1_URL,
      BAIDU_MAP_API_KEY: pageProps?.BAIDU_MAP_API_KEY,
      REQUEST_ID: requestId,
      ACCEPT_LANGUAGE: currentLocale,
    }),
    [
      pageProps?.SESSION_GET_CALL_URL_CLIENT,
      pageProps?.NEXT_PUBLIC_SUBMIT_SEARCH_URL,
      pageProps?.NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
      pageProps?.GOOGLE_MAP_API_KEY,
      pageProps?.UXL_IMAGE_CACHE_DOMAIN,
      pageProps?.SESSION_APP_GET_CALL_V1_URL,
      pageProps?.BAIDU_MAP_API_KEY,
      requestId,
      currentLocale,
    ]
  );

  const memoizedFeatureFlags = useMemo(() => ({ ...featureFlags, featureFlagLogo }), [featureFlags, featureFlagLogo]);

  if (deferHydration) {
    // FLAG ON: all operations run synchronously (matching flag OFF behavior)
    // to ensure data + DOM setup are ready before children mount
    setPageModel(respGridModel);
    setEnvVars(deferredEnvVars);
    mergeEnvVars(deferredMergedEnvVars);
    if (model) {
      setSubDirectoryPrefix(model.subDirectoryContextPath ? model.subDirectoryContextPath : '');
    }

    if (canUseDOM) {
      populatePIdataLayer();
    }

    if (!!responsivegrid && !isAuthorMode) {
      initialize(responsivegrid);
    }

    featureFlags = memoizedFeatureFlags;
    const allowedComponents = responsivegrid?.allowedComponents?.components;
    if (!componentMappingInitialized.current) {
      defineComponentMapping(allowedComponents, pagePath, isAuthorMode, pageProps, featureFlags);
      componentMappingInitialized.current = true;
    }
  } else {
    // FLAG OFF: Original synchronous code path (exact trunk behavior)
    const respGridModelClone = JSON.parse(JSON.stringify(respGridModel)); // Deep clone the model
    setPageModel(respGridModelClone);

    const envVars = apolloEnvVars ? apolloEnvVars : {};
    envVars['NEXT_PUBLIC_FETCH_OFFERS_API_URL'] = NEXT_PUBLIC_FETCH_OFFERS_API_URL;
    envVars['TARGET_PROPERTY_TOKEN'] = TARGET_PROPERTY_TOKEN;
    envVars['SESSION_APP_POST_CALL_URL'] = pageProps?.SESSION_APP_POST_CALL_URL;
    envVars['NEXT_PUBLIC_PREFIX'] = pageProps?.NEXT_PUBLIC_PREFIX;
    envVars['NEXT_PUBLIC_ENABLE_DCA_V2'] = getEnvProp(envVarKeys.NEXT_PUBLIC_ENABLE_DCA_V2);
    envVars['NEXT_PUBLIC_ENABLE_WEBSDK'] = isWebSDKEnabled;
    envVars['GET_DESTINATION_TYPE_FLAG'] = pageProps?.GET_DESTINATION_TYPE_FLAG;
    setEnvVars(envVars);

    if (canUseDOM) {
      populatePIdataLayer();
    }

    if (!!responsivegrid && !isAuthorMode) {
      initialize(responsivegrid);
    }

    featureFlags = { ...featureFlags, featureFlagLogo };

    const allowedComponents = responsivegrid?.allowedComponents?.components;
    defineComponentMapping(allowedComponents, pagePath, isAuthorMode, pageProps, featureFlags);

    mergeEnvVars({
      SESSION_GET_CALL_URL_CLIENT: pageProps?.SESSION_GET_CALL_URL_CLIENT,
      NEXT_PUBLIC_SUBMIT_SEARCH_URL: pageProps?.NEXT_PUBLIC_SUBMIT_SEARCH_URL,
      NEXT_PUBLIC_AVAILABILITY_SEARCH_URL: pageProps?.NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
      GOOGLE_MAP_API_KEY: pageProps?.GOOGLE_MAP_API_KEY,
      UXL_IMAGE_CACHE_DOMAIN: pageProps?.UXL_IMAGE_CACHE_DOMAIN,
      SESSION_APP_GET_CALL_V1_URL: pageProps?.SESSION_APP_GET_CALL_V1_URL,
      BAIDU_MAP_API_KEY: pageProps?.BAIDU_MAP_API_KEY,
      REQUEST_ID: requestId,
      ACCEPT_LANGUAGE: currentLocale,
    });

    if (model) {
      setSubDirectoryPrefix(model.subDirectoryContextPath ? model.subDirectoryContextPath : '');
    }
  }

  // Author mode stylesheet injection (unchanged, always in useEffect)
  useEffect(() => {
    if (!isAuthorMode) {
      return;
    }
    let remoteURL: URL | string;
    try {
      remoteURL = new URL(document.body.dataset.remoteUrl || '');
      remoteURL = remoteURL.origin;
    } catch (e) {
      remoteURL = '';
    }
    const assetUrl = `${process.env.NEXT_PUBLIC_ASSET_DOMAIN || remoteURL || prodDNS}${NEXT_GLOBAL_ASSET_PREFIX}`;
    const globalStyle = document.createElement('link');

    globalStyle.rel = 'stylesheet';
    globalStyle.href =
      assetUrl +
      (model?.rtlEnable == 'true'
        ? `global-styles/${version}/marriot.global.rtl.min.css`
        : `global-styles/${version}/marriot.global.min.css`);
    document.head.appendChild(globalStyle);
  }, [isAuthorMode, NEXT_GLOBAL_ASSET_PREFIX, model?.rtlEnable]);

  // Session data update (unchanged, always in useEffect)
  useEffect(() => {
    updateGlobalData('sessionData', pageProps?.sessionData);
  }, [pageProps?.sessionData, updateGlobalData, isWebSDKEnabled]);

  Object.defineProperty(rtlPluginTs, 'name', { value: 'rtlPluginMonorepo' });

  return (
    <>
      {devBrandCode && pageProps?.INJECT_BRAND_CSS === 'true' && (
        <link
          rel="stylesheet"
          href={`https://www.marriott.com/etc.clientlibs/mcom-book-spa/brandingcontent.${devBrandCode}.css`}
          as="style"
        />
      )}
      {process.env.NODE_ENV !== 'test' ? (
        <StyleSheetManager stylisPlugins={pageProps?.model?.rtlEnable === 'true' ? [rtlPluginTs] : []}>
          <UserSessionProvider pageProps={pageProps} includePiData>
            <DynamicDCAPageProvider Component={Component} pageProps={pageProps} />
          </UserSessionProvider>
        </StyleSheetManager>
      ) : (
        <UserSessionProvider pageProps={pageProps} includePiData>
          <DynamicDCAPageProvider Component={Component} pageProps={pageProps} />
        </UserSessionProvider>
      )}
    </>
  );
};

export default App;
